'use client';

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import {
  type CartItem,
  loadCartFromCookie,
  saveCartToCookie,
  canAddCartItem,
  reconcileCartWithOwnedItems,
} from '@/lib/cart-storage';
import { syncCart } from '@/lib/cart-sync';
import { paymentApi } from '@/lib/api/payments';
import { useAuth } from './AuthContext';

export type { CartItem };

// Debounce window for the background cart-sync side effect — avoids firing a request per
// keystroke-speed add/remove burst. Purely an analytics side effect; never blocks cart UX.
const CART_SYNC_DEBOUNCE_MS = 2000;

interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (moduleId: string) => void;
  clearCart: () => void;
  isInCart: (moduleId: string) => boolean;
  totalAmount: number;
  itemCount: number;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  /** Fire-and-forget: tell the backend a checkout was just initiated for the current cart. */
  notifyCheckoutInitiated: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const { isAuthenticated } = useAuth();
  // Unlike landing-page, dashboard has no app-wide OwnedItemsContext — fetched
  // directly here, scoped to what cart reconciliation needs. See
  // reconcileCartWithOwnedItems's comment for why this exists.
  const [ownedIds, setOwnedIds] = useState<{ moduleIds: string[]; courseIds: string[]; bundleIds: string[] } | null>(
    null,
  );

  const hydrate = useCallback(() => {
    setItems(loadCartFromCookie());
  }, []);

  useEffect(() => {
    hydrate();
    setHydrated(true);
  }, [hydrate]);

  const refreshOwnedIds = useCallback(() => {
    if (!isAuthenticated) return;
    paymentApi
      .getOwnedItems()
      .then((data) => setOwnedIds(data))
      .catch(() => {
        // Silent fail — reconciliation just skips this cycle.
      });
  }, [isAuthenticated]);

  // Cookies don't fire a `storage` event like localStorage did, so cross-tab/
  // cross-app reactivity (e.g. landing adding an item, or a purchase paid in
  // another tab) is picked up by re-reading the cart AND re-fetching
  // ownership on focus and visibility change instead.
  useEffect(() => {
    const handleRefresh = () => {
      hydrate();
      refreshOwnedIds();
    };
    window.addEventListener('focus', handleRefresh);
    document.addEventListener('visibilitychange', handleRefresh);
    return () => {
      window.removeEventListener('focus', handleRefresh);
      document.removeEventListener('visibilitychange', handleRefresh);
    };
  }, [hydrate, refreshOwnedIds]);

  useEffect(() => {
    if (hydrated) saveCartToCookie(items);
  }, [items, hydrated]);

  // Fetch ownership on auth change (mirrors landing-page's OwnedItemsContext
  // fetch triggers) — silent fail is deliberate, same as that context: the
  // reconcile effect below just no-ops until a later fetch succeeds.
  useEffect(() => {
    if (!isAuthenticated) {
      setOwnedIds(null);
      return;
    }
    refreshOwnedIds();
  }, [isAuthenticated, refreshOwnedIds]);

  // Reconcile against ownership once it's loaded — drops any module/course/
  // bundle the user already owns instead of leaving it in the cart forever
  // inflating the header badge. Runs after hydrate so it never fires against
  // the empty pre-hydration state and wipes a real cart.
  useEffect(() => {
    if (!hydrated || !ownedIds) return;
    setItems((prev) => {
      const reconciled = reconcileCartWithOwnedItems(prev, ownedIds);
      return reconciled.length === prev.length ? prev : reconciled;
    });
  }, [hydrated, ownedIds]);

  // Fire-and-forget backend sync, debounced — a pure analytics side effect layered on top of
  // the cookie persistence above, never blocking or affecting cart UX. Empty cart (explicit
  // clear, or the last item removed) reports CLEARED instead of ACTIVE.
  useEffect(() => {
    if (!hydrated) return;
    const timer = setTimeout(() => {
      syncCart(items, items.length === 0 ? 'CLEARED' : 'ACTIVE');
    }, CART_SYNC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [items, hydrated]);

  const addItem = useCallback((item: CartItem) => {
    setItems((prev) => {
      if (prev.some((i) => i.moduleId === item.moduleId)) return prev;
      if (!canAddCartItem(prev)) return prev;
      return [...prev, item];
    });
  }, []);

  const removeItem = useCallback((moduleId: string) => {
    setItems((prev) => prev.filter((i) => i.moduleId !== moduleId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback(
    (moduleId: string) => items.some((i) => i.moduleId === moduleId),
    [items]
  );

  const totalAmount = useMemo(
    () => items.reduce((sum, i) => sum + i.price, 0),
    [items]
  );

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // Immediate (non-debounced) — checkout initiation is a discrete event, not a state change
  // to react to, so it doesn't go through the items-change effect above.
  const notifyCheckoutInitiated = useCallback(() => {
    syncCart(items, 'CHECKED_OUT');
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        clearCart,
        isInCart,
        totalAmount,
        itemCount: items.length,
        isDrawerOpen,
        openDrawer,
        closeDrawer,
        notifyCheckoutInitiated,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
